<template>
  <hs-modal v-model="open" title="Invite Member" size="lg" @close="$emit('close')">
    <form id="igmm-form" class="igmm-form" @submit.prevent="handleSubmit">
      <hs-alert
        tone="info"
        title="Invite a user to join this group"
        message="Select a store in this group, then choose a user to invite."
      />

      <hs-select
        v-model="form.storeId"
        label="Store"
        :placeholder="storesLoading ? 'Loading stores…' : 'Select a group store'"
        :options="storeOptions"
        :disabled="loading || storesLoading"
        :error="storesError"
        empty-message="No stores in this group"
        searchable
        search-placeholder="Search stores…"
        required
        class="igmm-field"
      />
      <hs-select
        v-if="form.storeId"
        v-model="form.userId"
        label="Store User"
        :placeholder="usersLoading ? 'Loading users…' : 'Select a store user'"
        :options="userOptions"
        :disabled="loading || usersLoading"
        :error="usersError"
        empty-message="No users found in this store"
        searchable
        search-placeholder="Search by name, phone, or email…"
        required
      />

      <group-permissions-selector
        v-model="form.permissions"
        :catalog="permissionCatalog"
        :loading="catalogLoading"
        :disabled="loading"
      />

      <hs-alert v-if="errorMessage" tone="danger" :message="errorMessage" />
    </form>

    <template #footer>
      <div class="igmm-footer">
        <hs-button variant="ghost" type="button" :disabled="loading" @click="$emit('close')">
          Cancel
        </hs-button>
        <hs-button
          variant="primary"
          icon="user-plus"
          type="submit"
          form="igmm-form"
          :loading="loading || catalogLoading"
          :disabled="loading || catalogLoading || usersLoading || !form.userId"
        >
          {{ catalogLoading ? "Loading…" : "Invite Member" }}
        </hs-button>
      </div>
    </template>
  </hs-modal>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import api from "@/helper/api";
import GroupPermissionsSelector from "./GroupPermissionsSelector.vue";
import { HsModal, HsButton, HsSelect, HsAlert } from "@/components/ui";

export default {
  name: "InviteGroupMemberModal",
  components: { HsModal, HsButton, HsSelect, HsAlert, GroupPermissionsSelector },
  props: {
    groupId: { type: Number, required: true },
  },
  emits: ["close", "invited"],
  setup(props, { emit }) {
    const open = ref(true);
    const loading = ref(false);
    const catalogLoading = ref(true);
    const errorMessage = ref("");
    const permissionCatalog = ref({});

    const form = ref({ storeId: "", userId: "", permissions: [] });
    const stores = ref([]);
    const storeUsers = ref([]);
    const storesLoading = ref(true);
    const usersLoading = ref(false);
    const storesError = ref("");
    const usersError = ref("");
    let usersRequest = 0;
    let disposed = false;
    const storeOptions = computed(() => stores.value.map((store) => ({
      value: store.store_id,
      label: store.name || `Store #${store.store_id}`,
    })));
    const userOptions = computed(() => storeUsers.value.map(({ user }) => ({
      value: user.id,
      label: [user.names || "Unnamed user", user.phone_number, user.email].filter(Boolean).join(" · "),
    })));

    const loadStores = async () => {
      try {
        let page = 1;
        const items = [];
        let hasNext;
        do {
          const { data } = await http.get(`/v2/groups/${props.groupId}/stores`, {
            params: { page, per_page: 100 },
          });
          if (disposed) return;
          items.push(...(data.items || []).filter((store) => !store.deleted));
          hasNext = data.has_next;
          page += 1;
        } while (hasNext);
        stores.value = items;
      } catch (err) {
        if (!disposed) storesError.value = err?.response?.data?.error?.message || "Unable to load group stores.";
      } finally {
        if (!disposed) storesLoading.value = false;
      }
    };

    watch(() => form.value.storeId, async (storeId) => {
      const request = ++usersRequest;
      form.value.userId = "";
      storeUsers.value = [];
      usersError.value = "";
      errorMessage.value = "";
      usersLoading.value = false;
      if (!storeId) return;
      usersLoading.value = true;
      try {
        const { data } = await http.get(api.store_users_permissions(storeId));
        if (disposed || request !== usersRequest) return;
        storeUsers.value = (Array.isArray(data) ? data : []).filter((entry) => entry.user?.id);
      } catch (err) {
        if (disposed || request !== usersRequest) return;
        usersError.value = err?.response?.data?.error?.message || "Unable to load users. You need Manage Users permission for this store.";
      } finally {
        if (!disposed && request === usersRequest) usersLoading.value = false;
      }
    });
    onBeforeUnmount(() => { disposed = true; ++usersRequest; });

    const loadPermissionCatalog = async () => {
      catalogLoading.value = true;
      try {
        const { data } = await http.get(api.group_permissions_catalog);
        permissionCatalog.value = data || {};
        if (!form.value.permissions.length) {
          form.value.permissions = [...(permissionCatalog.value.MEMBER || [])];
        }
      } catch (err) {
        console.error("Failed to load group permission catalog", err);
        errorMessage.value = err?.response?.data?.error?.message || "Unable to load group permissions.";
      } finally {
        catalogLoading.value = false;
      }
    };

    const handleSubmit = async () => {
      if (loading.value || catalogLoading.value || usersLoading.value) return;
      const selectedUser = storeUsers.value.find((entry) => String(entry.user.id) === String(form.value.userId));
      if (!form.value.storeId || !selectedUser) { errorMessage.value = "Select a store and a user to invite."; return; }
      if (!form.value.permissions.length) { errorMessage.value = "Select at least one permission."; return; }

      loading.value = true;
      errorMessage.value = "";
      try {
        const payload = { user_id: selectedUser.user.id, permissions: [...form.value.permissions] };

        await http.post(`/v2/groups/${props.groupId}/members`, payload);
        toast.success("Member invited successfully");
        emit("invited");
        emit("close");
      } catch (err) {
        console.error("Failed to invite member", err);
        errorMessage.value = err?.response?.data?.error?.message || err.message || "Unable to invite member.";
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => { loadPermissionCatalog(); loadStores(); });

    return { storeOptions, userOptions, storesLoading, usersLoading, storesError, usersError, open, loading, catalogLoading, errorMessage, form, handleSubmit, permissionCatalog };
  },
};
</script>

<style scoped>
.igmm-form {
  display: grid;
  gap: 1rem;
}

.igmm-field {
  margin-top: 0.25rem;
}

.igmm-footer {
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
}
</style>
